import chroma from 'chroma-js';
import type { ButtonV2Props } from '@withjoy/joykit';

export const parseColor = (color?: string) => {
  if (!color) {
    return undefined;
  }
  try {
    return chroma(color);
  } catch (e) {
    return undefined;
  }
};

export const isWhite = (color?: string) => {
  if (!color) return false;
  try {
    return chroma(color).hex().toLowerCase() === '#ffffff';
  } catch (e) {
    return false;
  }
};

/**
 * Determines if a color is light or dark based on the HSP color model.
 * @param r red channel value 0-255
 * @param g green channel value 0-255
 * @param g blue channel value 0-255
 * @returns boolean true if the color is in the upper half of the HSP color range.
 */
export const isLightColor = (r: number, g: number, b: number): boolean => {
  const HALF_HSP_RANGE = 127.5;

  // HSP equation from http://alienryderflex.com/hsp.html
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

  // Determine whether the color is dark
  if (hsp < HALF_HSP_RANGE) {
    return false;
  }

  return true;
};

export const darkenColor = (color: string) => {
  // taken from monorepo
  return chroma(
    chroma(color)
      .rgb()
      .map(x => x * 0.8),
  ).hex();
};

/**
 * Nudges `color` a few tones away from the page background so a surface derived from the theme
 * color (e.g. the event-group-code banner) stays cohesive with the page but separates subtly instead of
 * blending in: light colors are darkened, dark colors are lightened. Amounts are in Lab units
 * (~1 ≈ 18 L*). The defaults are asymmetric to match design's reference examples — dark palettes
 * are lightened a bit more, light palettes darkened a bit less, since lightening a dark bar reads
 * more cleanly than darkening a light one. Falls back to the input on invalid color.
 */
export const shiftToneForSubtleContrast = (color: string, { lightenBy = 0.6, darkenBy = 0.35 }: { lightenBy?: number; darkenBy?: number } = {}): string => {
  try {
    const [r, g, b] = chroma(color).rgb();
    const shifted = isLightColor(r, g, b) ? chroma(color).darken(darkenBy) : chroma(color).brighten(lightenBy);
    return shifted.hex();
  } catch {
    return color;
  }
};

export const toRgbArray = (color: string): [number, number, number] => {
  return chroma(color).rgb();
};

export const hexToRgb = (hex: string): [number, number, number] => {
  return chroma(hex).rgb();
};

/**
 * Returns a near-black or white text color — whichever is legible on `background` — so text stays
 * readable on a themed surface whose own configured font color may not contrast (e.g. a hero
 * "art" font color reused on a solid bar). Falls back to white for invalid input.
 */
export const getReadableTextColor = (background: string, dark = '#1d1d1f', light = '#ffffff'): string => {
  try {
    const [r, g, b] = hexToRgb(background);
    return isLightColor(r, g, b) ? dark : light;
  } catch {
    return light;
  }
};

/**
 * Returns `backgroundColor` when it contrasts enough with `foregroundColor`; otherwise
 * returns a fallback background (dark `#333333` or light `#ffffff`) chosen to contrast
 * with the foreground. Used when a foreground element (e.g. a monogram drawn in the page
 * font color) must stay visible regardless of the configured background — e.g. when the
 * page background and font color are both white (→ `#333333`) or both black (→ `#ffffff`).
 */
const MIN_VISIBLE_CONTRAST = 2;

/** True when `foreground` is legible on `background` (WCAG-style contrast ratio ≥ `min`). */
export const hasReadableContrast = (background: string, foreground: string, min = MIN_VISIBLE_CONTRAST): boolean => {
  try {
    return chroma.contrast(background, foreground) >= min;
  } catch {
    return false;
  }
};

export const getContrastingBackground = (backgroundColor: string, foregroundColor: string): string => {
  try {
    if (chroma.contrast(backgroundColor, foregroundColor) >= MIN_VISIBLE_CONTRAST) {
      return backgroundColor;
    }
    // chroma luminance: 0 (black) → 1 (white). A light foreground needs a dark background and vice versa.
    return chroma(foregroundColor).luminance() > 0.5 ? '#333333' : '#ffffff';
  } catch {
    return backgroundColor;
  }
};

export const isWhiteishHex = (hex: string, threshold = 0.9): boolean => {
  try {
    const luminance = chroma(hex).luminance(); // 0 (black) → 1 (white)
    return luminance >= threshold;
  } catch {
    return false; // invalid color
  }
};

export type ColorRGBA = [r: number, g: number, b: number, a: number];
export type Color = ColorRGBA | string;

const transformToChromaColor = (color: Color): chroma.Color => {
  if (Array.isArray(color)) {
    const [r, g, b, a] = color;
    return chroma(r, g, b, a);
  }
  return chroma(color);
};

export const fadeColor = (color: Color, alpha: number): string => {
  const chromaColor = transformToChromaColor(color);
  return chromaColor.alpha(alpha).css();
};

/**
 * Contrast ratios can range from 1 to 21 (commonly written 1:1 to 21:1).
 *
 * https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-tests
 */
export const getContrastRatio = (foreground: Color, background: Color): number => {
  const lumA = transformToChromaColor(foreground).luminance();
  const lumB = transformToChromaColor(background).luminance();

  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);

  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Enhanced standards
 */
const CONTRAST_THRESHOLD = 4.5;
const CONTRAST_THRESHOLD_MIN = 3.0;
const LIGHT_FOREGROUND = '#333333';
const DARK_FOREGROUND = '#ffffff';

export const WCAG_LARGE_TEXT_CONTRAST_THRESHOLD = 3.0;

export const getContrastText = (background: Color): string => {
  const contrastText = getContrastRatio(DARK_FOREGROUND, background) >= CONTRAST_THRESHOLD ? DARK_FOREGROUND : LIGHT_FOREGROUND;
  if (process.env.NODE_ENV !== 'production') {
    const contrast = getContrastRatio(contrastText, background);
    if (contrast < CONTRAST_THRESHOLD_MIN) {
      console.error(`Contrast ratio of ${contrast}:1 for ${contrastText} on ${background} falls below the absolute minimum contrast threshold of 3.0`);
    }
  }

  return contrastText;
};

/** Use `candidate` if it meets `minContrast` on `background` (default WCAG AA 4.5:1; pass `WCAG_LARGE_TEXT_CONTRAST_THRESHOLD` for large/bold); otherwise `fallback`. */
export const pickAccessibleColor = (candidate: string | undefined, fallback: string, background = '#ffffff', minContrast = CONTRAST_THRESHOLD): string => {
  if (candidate && hasReadableContrast(background, candidate, minContrast)) {
    return candidate;
  }
  return fallback;
};

/** All-or-nothing: use every candidate only if each meets its threshold on `background` (default 4.5:1; override via `minContrasts`); otherwise fall all fields back together. */
export const pickAccessibleColorSet = <T extends Record<string, string | undefined>>(
  candidates: T,
  fallbacks: { readonly [K in keyof T]: string },
  background = '#ffffff',
  minContrasts: { readonly [K in keyof T]?: number } = {},
): { [K in keyof T]: string } => {
  const keys = Object.keys(candidates) as Array<keyof T>;
  const allReadable = keys.every(key => {
    const candidate = candidates[key];
    return !!candidate && hasReadableContrast(background, candidate, minContrasts[key] ?? CONTRAST_THRESHOLD);
  });

  return keys.reduce(
    (result, key) => {
      result[key] = allReadable ? (candidates[key] as string) : fallbacks[key];
      return result;
    },
    {} as { [K in keyof T]: string },
  );
};

export const mixColors = (c1: string, pct: number, c2: string): string => {
  try {
    return chroma.mix(c1, c2, (100 - pct) / 100, 'rgb').hex();
  } catch {
    return c2;
  }
};

export type ThemedColorBase = Readonly<{
  bg: chroma.Color;
  text: chroma.Color;
  /** RGB-space mix of bg → text at the given ratio, returned as a hex string. */
  mix: (ratio: number) => string;
}>;

export const parseThemedColors = (pageColor: string | undefined, pageFontColor: string | undefined): ThemedColorBase | null => {
  const bg = parseColor(pageColor);
  const text = parseColor(pageFontColor);
  if (!bg || !text) return null;
  return {
    bg,
    text,
    mix: (ratio: number) => chroma.mix(bg, text, ratio, 'rgb').hex(),
  };
};

// Both neutrals are picked by *measured* contrast, and the dark one is pure black rather than a
// near-black, because that is what makes the choice safe: contrast is a function of relative
// luminance alone, and solving 1.05/(L+0.05) = (L+0.05)/0.05 puts the worst case for the better of
// white/black at 4.58:1 — above AA for every possible fill. The shared `getContrastText` and
// `getReadableTextColor` helpers above instead fall back to a near-black (#333333 / #1d1d1f) without
// verifying it against the fill, which on a mid-luminance fill is worse than the white it replaces:
// accent #999999 darkens to #7a7a7a, where white is 4.29:1 but #333333 is only 2.94:1.
export const readableTextOnFill = (fill: string, preferred: string): string => {
  if (hasReadableContrast(fill, preferred, CONTRAST_THRESHOLD)) {
    return preferred;
  }
  return hasReadableContrast(fill, '#FFFFFF', CONTRAST_THRESHOLD) ? '#FFFFFF' : '#000000';
};

// `textColor` is the event's pageFontColor — authored for legibility against the page background,
// not against an accent-derived button fill, so it cannot be applied to the button unchecked. Each
// state gets its own fill (darkened / brightened / darkened again), so the text has to be picked per
// state rather than once: the event's own color is kept wherever it clears AA on that state's actual
// fill, and only falls back to a neutral where it doesn't.
//
// Applying it unchecked failed in both directions (RSVP-32). A dark pageFontColor on a mid accent
// gave #020231 on #445EFB — 4.02:1 on hover, 3.07:1 active — which is the reported screenshot. And
// because no resting `color` was set at all, a *light* accent left joykit's white on the darkened
// fill: 2.30:1 at rest, before any hover.
export const getSolidButtonStylesWithAccentAndTextColor = (accentColor: string, textColor: string): ButtonV2Props => {
  const darkenedColor = darkenColor(accentColor);
  const backgroundColorHover = chroma(accentColor).brighten(0.2).hex();
  const backgroundColorActive = chroma(accentColor).darken(0.2).hex();
  const textColorOn = (fill: string) => readableTextOnFill(fill, textColor);

  return {
    backgroundColor: darkenedColor,
    color: textColorOn(darkenedColor),
    _hover: { color: textColorOn(backgroundColorHover), backgroundColor: backgroundColorHover },
    _active: { color: textColorOn(backgroundColorActive), backgroundColor: backgroundColorActive },
  };
};
